import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSelectTheme } from "@/contexts/SelectThemeContext";

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

export default function StripePayment({ order }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState("");
  const [switching, setSwitching] = useState("");
  // In-flight guard — prevents React StrictMode double-invoke and any accidental
  // parallel launch. `true` means a launch is either in progress or completed.
  const launchedRef = useRef(false);
  const { isSelect } = useSelectTheme();

  const launch = async () => {
    if (launchedRef.current) return;
    launchedRef.current = true;
    setError("");
    try {
      const { data } = await api.post("/payments/stripe/checkout", {
        order_id: order.id,
        origin_url: window.location.origin,
      });
      setClientSecret(data.client_secret);
    } catch (err) {
      const msg = err.response?.data?.detail || "Couldn't open Stripe checkout";
      setError(msg);
      toast.error(msg);
      launchedRef.current = false;
    }
  };

  const switchTo = async (method) => {
    if (switching) return;
    setSwitching(method);
    try {
      await api.patch(`/orders/${order.id}/payment-method`, { payment_method: method });
      toast.success(`Switched to ${method === "venmo" ? "Venmo" : "Cash App"}`);
      // Full reload so PaymentInstructions re-fetches the order and renders ManualPayment
      window.location.href = `/payment/${order.id}`;
    } catch (err) {
      const msg = err.response?.data?.detail || "Couldn't switch payment method";
      toast.error(msg);
      setSwitching("");
    }
  };

  useEffect(() => {
    if (order?.status === "pending_payment") {
      launch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  // iter138.1 — dark R-1617 palette when Select tier. Matches Cart + Checkout.
  const shell = isSelect ? "bg-[#F1E5C7] text-[#1B1614] min-h-screen" : "";
  const eyebrow = isSelect
    ? "text-[11px] uppercase tracking-[0.22em] text-[#1B1614]/60 mb-3"
    : "text-[11px] uppercase tracking-[0.22em] text-stone mb-3";
  const muted = isSelect ? "text-[#1B1614]/70" : "text-stone";
  const strong = isSelect ? "text-[#1B1614]" : "text-charcoal";
  const priceAccent = isSelect ? "text-[#6E521A]" : "text-oxblood";
  const primaryBtn = isSelect
    ? "bg-[#DDA94E] text-[#1B1614] hover:bg-[#F1E5C7]"
    : "bg-charcoal text-bone hover:bg-oxblood";
  const cardBorder = isSelect ? "border border-[#D6C8A3] bg-[#EBE0BE]" : "border border-borderSubtle bg-white";
  const errorCard = isSelect
    ? "border border-[#DDA94E]/40 bg-[#DDA94E]/[0.08]"
    : "border border-oxblood/40 bg-oxblood/5";
  const errorText = isSelect ? "text-[#6E521A]" : "text-oxblood";

  if (order.status === "paid") {
    return (
      <div className={shell}>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="payment-page">
          <p className={`overline mb-3 ${priceAccent}`}>Paid</p>
          <h1 className={`font-serif text-5xl md:text-6xl tracking-tight mb-4 ${strong}`}>This order is paid.</h1>
          <p className={`mb-8 ${muted}`}>Order #{order.order_number} is in our fulfilment queue.</p>
          <Link
            to={`/orders/${order.id}`}
            className={`inline-block px-6 py-3 tracking-[0.22em] text-xs uppercase rounded-full transition-colors ${primaryBtn}`}
            data-testid="stripe-view-order-btn"
          >
            View order
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={shell}>
      <div className="max-w-3xl mx-auto px-6 py-10 md:py-14" data-testid="payment-page">
        <p className={eyebrow}>Step 3 of 3 · Awaiting payment</p>
        <h1 className={`font-serif text-4xl md:text-5xl tracking-tight mb-2 ${strong}`}>Pay with card</h1>
        <p className={`mb-2 ${muted}`}>Order #{order.order_number}</p>
        <p className={`font-mono text-3xl mb-8 ${priceAccent}`}>${order.total.toFixed(2)}</p>

        {error && (
          <div className={`mb-6 p-5 rounded-2xl ${errorCard}`} data-testid="stripe-error-card">
            <p className={`overline mb-2 ${errorText}`}>Card checkout couldn't open</p>
            <p className={`text-sm mb-3 ${errorText}`}>{error}</p>
            <p className={`text-sm mb-4 ${muted}`}>
              If your card was declined, this is almost always your bank blocking a
              new merchant — a quick call to them usually clears it. Or switch to
              Venmo / Cash App below and pay in about 30 seconds.
            </p>
            <button
              onClick={() => { launchedRef.current = false; launch(); }}
              className={`text-[11px] uppercase tracking-[0.22em] underline ${errorText}`}
              data-testid="stripe-retry-btn"
            >
              Try card again
            </button>
          </div>
        )}

        {!stripePromise && (
          <p className={`text-sm ${muted}`}>Stripe not configured on this environment.</p>
        )}

        {stripePromise && clientSecret && (
          <div className={`rounded-2xl overflow-hidden ${cardBorder}`} data-testid="stripe-embedded-checkout">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        )}

        {stripePromise && !clientSecret && !error && (
          <div className={`rounded-2xl p-10 text-center ${cardBorder}`}>
            <p className={`overline ${muted}`}>Loading secure card form…</p>
          </div>
        )}

        <p className={`text-[11px] mt-6 text-center ${muted}`}>
          Card processing by Stripe. Your card details never touch our servers. Visa · Mastercard · Amex · Apple Pay.
        </p>

        {/* Friendly decline fallback — persistent nudge to manual payment.
            Visible even if Stripe iframe rejects the card mid-flow, since we
            can't observe iframe-side errors. */}
        <div
          className={`mt-8 rounded-2xl p-6 md:p-7 ${cardBorder}`}
          data-testid="stripe-fallback-panel"
        >
          <p className={`overline mb-2 ${priceAccent}`}>Card declined or having trouble?</p>
          <p className={`text-sm mb-5 ${muted}`}>
            Banks often block first-time larger purchases from new merchants — a quick
            call to the number on the back of your card usually fixes it. Or skip the
            bank entirely and pay <span className={strong}>Venmo</span> or{" "}
            <span className={strong}>Cash App</span> — same amount, instant, no card
            needed. Your order <span className={`font-mono ${strong}`}>#{order.order_number}</span> stays reserved either way.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => switchTo("venmo")}
              disabled={!!switching}
              className={`flex-1 py-3 tracking-[0.22em] text-[11px] uppercase rounded-full transition-colors disabled:opacity-50 ${primaryBtn}`}
              data-testid="switch-to-venmo-btn"
            >
              {switching === "venmo" ? "Switching…" : "Pay with Venmo"}
            </button>
            <button
              onClick={() => switchTo("cashapp")}
              disabled={!!switching}
              className={`flex-1 py-3 tracking-[0.22em] text-[11px] uppercase rounded-full transition-colors border disabled:opacity-50 ${
                isSelect
                  ? "border-[#DDA94E]/50 text-[#1B1614] hover:bg-[#DDA94E] hover:text-[#1B1614]"
                  : "border-charcoal text-charcoal hover:bg-charcoal hover:text-bone"
              }`}
              data-testid="switch-to-cashapp-btn"
            >
              {switching === "cashapp" ? "Switching…" : "Pay with Cash App"}
            </button>
          </div>
          <p className={`text-[11px] mt-4 ${muted}`}>
            Still stuck? Email{" "}
            <a
              href={`mailto:support@bulkbuykits.com?subject=Order%20${order.order_number}%20payment%20help`}
              className={`underline ${errorText}`}
              data-testid="stripe-fallback-support-mail"
            >
              support@bulkbuykits.com
            </a>{" "}
            and we'll invoice you directly.
          </p>
        </div>
      </div>
    </div>
  );
}
